import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Html5Qrcode } from "html5-qrcode";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Camera, CameraOff, Gift, Loader2, LogOut, ScanLine, User, X,
} from "lucide-react";
import { formatRewardSummary } from "@/lib/rewardDisplay";

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

const ALL_PERMS = { can_add_points: true, can_issue_reward: true, can_redeem: true };

export default function ScanPage() {
  const { user, logout, authHeaders, permissions: ctxPerms, isStaff, isVendor } = useAuth();
  const permissions = ctxPerms || user?.permissions || (isVendor ? ALL_PERMS : {});
  const [vendorName, setVendorName] = useState("");
  const [rewardLogic, setRewardLogic] = useState("spend_based");
  const [scanning, setScanning] = useState(false);
  const [manualId, setManualId] = useState("");
  const [customer, setCustomer] = useState(null);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [busy, setBusy] = useState(false);

  const [earnTotal, setEarnTotal] = useState("");
  const [earnStamps, setEarnStamps] = useState("1");
  const [rwdValue, setRwdValue] = useState("");
  const [rwdLabel, setRwdLabel] = useState("Till Reward");

  const html5Ref = useRef(null);
  const lastScanRef = useRef("");
  const scanLockRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/scan/me`, { headers: authHeaders() });
        if (cancelled) return;
        setVendorName(data.vendor?.name || "");
        setRewardLogic(data.vendor?.reward_logic || "spend_based");
      } catch {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, [authHeaders]);

  const stopScanner = useCallback(async () => {
    const inst = html5Ref.current;
    html5Ref.current = null;
    setScanning(false);
    if (inst) {
      try {
        if (inst.isScanning) await inst.stop();
      } catch { /* ignore */ }
      try { await inst.clear(); } catch { /* ignore */ }
    }
  }, []);

  const resolvePayload = useCallback(async (payload) => {
    const text = (payload || "").trim();
    if (!text || scanLockRef.current) return;
    if (text === lastScanRef.current && customer) return;
    scanLockRef.current = true;
    setLoadingCustomer(true);
    try {
      await stopScanner();
      const { data } = await axios.post(
        `${API}/scan/resolve`,
        { payload: text },
        { headers: authHeaders() },
      );
      lastScanRef.current = text;
      setCustomer(data);
      setRewardLogic(data.reward_logic || rewardLogic);
      toast.success(`Loaded ${data.name || "customer"}`);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not resolve QR");
    } finally {
      setLoadingCustomer(false);
      scanLockRef.current = false;
    }
  }, [authHeaders, customer, rewardLogic, stopScanner]);

  const startScanner = useCallback(async () => {
    if (scanning) return;
    setCustomer(null);
    lastScanRef.current = "";
    setScanning(true);
    await new Promise((r) => setTimeout(r, 50));
    try {
      const inst = new Html5Qrcode("scan-qr-reader");
      html5Ref.current = inst;
      await inst.start(
        { facingMode: "environment" },
        { fps: 8, qrbox: { width: 240, height: 240 } },
        (decoded) => { resolvePayload(decoded); },
        () => {},
      );
    } catch (err) {
      setScanning(false);
      toast.error(err?.message || "Camera unavailable — paste an ID instead");
    }
  }, [scanning, resolvePayload]);

  useEffect(() => () => { stopScanner(); }, [stopScanner]);

  const refreshCustomer = async (id) => {
    const { data } = await axios.get(`${API}/scan/customer/${id}`, { headers: authHeaders() });
    setCustomer(data);
    setRewardLogic(data.reward_logic || rewardLogic);
  };

  const handleManualLookup = async (e) => {
    e?.preventDefault();
    if (!manualId.trim()) return;
    await resolvePayload(manualId.trim());
  };

  const handleEarn = async () => {
    if (!customer) return;
    setBusy(true);
    try {
      const body = { customer_id: customer.id };
      if ((customer.reward_logic || rewardLogic) === "order_based") {
        body.stamps = parseInt(earnStamps, 10) || 1;
        body.total = parseFloat(earnTotal) || 0;
      } else {
        body.total = parseFloat(earnTotal);
        if (Number.isNaN(body.total)) {
          toast.error("Enter a £ amount");
          setBusy(false);
          return;
        }
      }
      const { data } = await axios.post(`${API}/scan/earn`, body, { headers: authHeaders() });
      toast.success(
        data.reward_issued
          ? `Earned ${data.points_earned} — reward issued!`
          : `Earned ${data.points_earned}`,
      );
      setEarnTotal("");
      setEarnStamps("1");
      await refreshCustomer(customer.id);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const handleIssueReward = async () => {
    if (!customer) return;
    const value = parseFloat(rwdValue);
    if (Number.isNaN(value)) {
      toast.error("Enter a reward value");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/scan/issue-reward`,
        { customer_id: customer.id, value_gbp: value, label: rwdLabel || "Till Reward" },
        { headers: authHeaders() },
      );
      toast.success("Reward issued");
      setRwdValue("");
      setRwdLabel("Till Reward");
      await refreshCustomer(customer.id);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const handleRedeem = async (rewardId) => {
    if (!customer) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/scan/redeem`,
        { customer_id: customer.id, reward_id: rewardId },
        { headers: authHeaders() },
      );
      toast.success("Reward redeemed");
      await refreshCustomer(customer.id);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const clearCustomer = () => {
    setCustomer(null);
    lastScanRef.current = "";
  };

  const logic = customer?.reward_logic || rewardLogic;
  const isOrderBased = logic === "order_based";
  const balanceLabel = isOrderBased ? "Stamps" : "Points";

  return (
    <div className="min-h-screen bg-surface font-body flex flex-col" data-testid="scan-page">
      <header className="bg-white border-b border-line px-4 py-3 flex items-center justify-between gap-3 shrink-0">
        <div className="min-w-0">
          <p className="text-xs text-quiet uppercase tracking-widest">Till scanner</p>
          <h1 className="text-base font-bold text-ink truncate font-display">{vendorName || "Scan"}</h1>
          <p className="text-xs text-quiet truncate">
            {user?.name || "Staff"}{isStaff ? " · Staff" : isVendor ? " · Vendor" : ""}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => { logout(); window.location.href = "/login"; }}
          className="text-quiet hover:text-danger hover:bg-purple-50 gap-1.5"
          data-testid="scan-logout"
        >
          <LogOut size={14} /> Logout
        </Button>
      </header>

      <main className="flex-1 max-w-lg mx-auto w-full p-4 space-y-4">
        {!customer && (
          <div className="space-y-4">
            <div className="rounded-2xl bg-white shadow-sm border border-line overflow-hidden">
              <div className="px-4 py-3 flex items-center justify-between border-b border-line">
                <div className="flex items-center gap-2 text-sm font-semibold text-ink">
                  <ScanLine size={16} /> Scan customer QR
                </div>
                {scanning ? (
                  <Button size="sm" variant="outline" className="h-8 gap-1 rounded-pill" onClick={stopScanner} data-testid="stop-camera">
                    <CameraOff size={14} /> Stop
                  </Button>
                ) : (
                  <Button size="sm" className="h-8 gap-1 rounded-pill bg-purple-500 hover:bg-purple-600 text-white" onClick={startScanner} data-testid="start-camera">
                    <Camera size={14} /> Camera
                  </Button>
                )}
              </div>
              <div
                id="scan-qr-reader"
                className={`bg-surface ${scanning ? "min-h-[260px]" : "hidden"}`}
              />
              {!scanning && (
                <div className="px-4 py-10 text-center text-sm text-quiet">
                  Start the camera or paste a customer ID / QR payload below
                </div>
              )}
            </div>

            <form onSubmit={handleManualLookup} className="rounded-2xl bg-white shadow-sm border border-line p-4 space-y-3">
              <Label className="text-xs font-semibold text-quiet uppercase tracking-widest">
                Manual lookup
              </Label>
              <Input
                value={manualId}
                onChange={(e) => setManualId(e.target.value)}
                placeholder="Customer ID, QR text, or phone"
                className="h-11 rounded-xl"
                data-testid="manual-scan-input"
              />
              <Button
                type="submit"
                disabled={loadingCustomer || !manualId.trim()}
                className="w-full h-11 rounded-pill bg-purple-500 hover:bg-purple-600 text-white"
                data-testid="manual-scan-submit"
              >
                {loadingCustomer ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                Look up
              </Button>
            </form>
          </div>
        )}

        {loadingCustomer && !customer && (
          <div className="flex justify-center py-8"><Loader2 className="animate-spin text-quiet" /></div>
        )}

        {customer && (
          <div className="space-y-4" data-testid="scan-customer-panel">
            <div className="rounded-2xl bg-white shadow-sm border border-line p-5 relative">
              <button
                type="button"
                onClick={clearCustomer}
                className="absolute top-3 right-3 p-1.5 rounded-full text-quiet hover:text-ink hover:bg-purple-50"
                data-testid="clear-customer"
                aria-label="Clear"
              >
                <X size={16} />
              </button>
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-full bg-purple-50 flex items-center justify-center text-purple-500 shrink-0">
                  <User size={20} />
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl font-bold text-ink truncate font-display">{customer.name}</h2>
                  <p className="text-xs text-quiet truncate">{customer.email || customer.mobile_number || customer.id}</p>
                  <p className="mt-2 text-2xl font-bold text-ink">
                    {customer.point_balance ?? 0}
                    <span className="text-sm font-medium text-quiet ml-1.5">{balanceLabel}</span>
                  </p>
                </div>
              </div>
            </div>

            {permissions.can_add_points && (
              <div className="rounded-2xl bg-white shadow-sm border border-line p-4 space-y-3" data-testid="earn-panel">
                <h3 className="text-sm font-semibold text-ink">Add {isOrderBased ? "stamps" : "points"}</h3>
                {isOrderBased ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-quiet">Stamps</Label>
                      <Input
                        type="number"
                        min="1"
                        value={earnStamps}
                        onChange={(e) => setEarnStamps(e.target.value)}
                        className="h-10 rounded-xl"
                        data-testid="earn-stamps-input"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-quiet">Spend £ (optional)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={earnTotal}
                        onChange={(e) => setEarnTotal(e.target.value)}
                        className="h-10 rounded-xl"
                        data-testid="earn-total-optional"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <Label className="text-xs text-quiet">Spend amount (£)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={earnTotal}
                      onChange={(e) => setEarnTotal(e.target.value)}
                      placeholder="e.g. 12.50"
                      className="h-10 rounded-xl"
                      data-testid="earn-total-input"
                    />
                  </div>
                )}
                <Button
                  onClick={handleEarn}
                  disabled={busy}
                  className="w-full h-10 rounded-pill bg-purple-500 hover:bg-purple-600 text-white"
                  data-testid="earn-submit"
                >
                  {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
                  Submit earn
                </Button>
              </div>
            )}

            {permissions.can_issue_reward && (
              <div className="rounded-2xl bg-white shadow-sm border border-line p-4 space-y-3" data-testid="issue-panel">
                <h3 className="text-sm font-semibold text-ink flex items-center gap-1.5">
                  <Gift size={14} /> Issue reward
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-quiet">Value (£)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={rwdValue}
                      onChange={(e) => setRwdValue(e.target.value)}
                      className="h-10 rounded-xl"
                      data-testid="issue-value-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-quiet">Label</Label>
                    <Input
                      value={rwdLabel}
                      onChange={(e) => setRwdLabel(e.target.value)}
                      className="h-10 rounded-xl"
                      data-testid="issue-label-input"
                    />
                  </div>
                </div>
                <Button
                  onClick={handleIssueReward}
                  disabled={busy}
                  variant="outline"
                  className="w-full h-10 rounded-pill"
                  data-testid="issue-submit"
                >
                  Issue reward
                </Button>
              </div>
            )}

            <div className="rounded-2xl bg-white shadow-sm border border-line p-4 space-y-3" data-testid="redeem-panel">
              <h3 className="text-sm font-semibold text-ink">Active rewards</h3>
              {(customer.active_rewards || []).length === 0 && (
                <p className="text-sm text-quiet py-2">No active rewards</p>
              )}
              <ul className="space-y-2">
                {(customer.active_rewards || []).map((r) => {
                  const preview = formatRewardSummary(r);
                  return (
                  <li
                    key={r.id}
                    className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl bg-surface"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-ink truncate">{preview.title}</p>
                      <p className="text-xs text-quiet truncate">
                        {preview.summary}
                      </p>
                      {preview.reward_type === "item_choice" && preview.item_options?.length > 0 && (
                        <p className="text-[11px] text-quiet truncate">
                          {preview.item_options.join(", ")}
                        </p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      disabled={!permissions.can_redeem || busy}
                      onClick={() => handleRedeem(r.id)}
                      className="h-8 rounded-pill bg-purple-500 hover:bg-purple-600 text-white shrink-0"
                      data-testid={`redeem-${r.id}`}
                    >
                      Redeem
                    </Button>
                  </li>
                  );
                })}
              </ul>
              {!permissions.can_redeem && (customer.active_rewards || []).length > 0 && (
                <p className="text-xs text-quiet">You do not have redeem permission</p>
              )}
            </div>

            <Button
              variant="outline"
              onClick={() => { clearCustomer(); startScanner(); }}
              className="w-full h-11 rounded-pill"
              data-testid="scan-next"
            >
              <ScanLine size={14} className="mr-2" /> Scan next customer
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
